import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const SECTIONS = [
  {
    title: "Data sources",
    body: "MarketTrace ingests public data directly from Polymarket via the Gamma API (markets & events), the Data API (holders, positions, trades, activity), and the CLOB API (order books & prices). Raw payloads are preserved so calculations can be improved retroactively without re-fetching.",
  },
  {
    title: "Trader scoring",
    body: "A weighted 0–100 score composed of risk-adjusted performance (25%), P&L consistency (20%), category performance (15%), entry-price quality (15%), resolved sample size (10%), drawdown control (10%), and recent performance (5%). Missing components are excluded and remaining weights are redistributed. A sample-size confidence penalty is applied when history is thin.",
  },
  {
    title: "Consensus",
    body: "Per active market, MarketTrace measures directional agreement across tracked traders, weighted by each trader's score and position size. A concentration penalty is applied when a small number of wallets account for most of the tracked exposure.",
  },
  {
    title: "Signals",
    body: "A signal is generated when consensus meets the configured thresholds. Signal types include multi-trader convergence, high-quality trader entry, unusual conviction increase, and rapid consensus change. Signals are never presented as 'Buy' or 'Sell' — they describe observed activity.",
  },
  {
    title: "Actionability",
    body: "Actionability estimates whether observed activity may still be relevant at the current market price, based on the movement between the estimated weighted-average entry and current probability, signal age, liquidity, and time to market close. It is not a guaranteed expected return.",
  },
  {
    title: "Missing-data handling",
    body: "Values are labeled exact, estimated, partial, or unavailable. Missing performance is never converted to zero. Traders with insufficient history are marked and receive a confidence penalty rather than a fabricated score.",
  },
  {
    title: "Data freshness & pipeline",
    body: "MarketTrace syncs live from Polymarket APIs on a continuous schedule. Markets are refreshed every 5 minutes, trader positions every 10 minutes, and signals every 15 minutes. A production trust health endpoint monitors all pipeline stages: market sync freshness, referenced market coverage, price sampling, signal generation, resolution processing, and backup recency. Stale or boundary-priced markets are excluded from actionable signals. The Admin page shows the current health status of every pipeline stage.",
  },
  {
    title: "Signal scoring & confidence",
    body: "Each signal receives a confidence label (High / Medium / Low) based on trader count, data completeness, consensus concentration, and actionability. The confidence layer is computed per-signal at query time using the latest market consensus snapshot. Signals are deduplicated by (market_id, outcome) so a single market never appears twice on the feed. The default feed shows only current opportunities — signals tied to live, active markets. Historical and all-market scopes are available via filter.",
  },
  {
    title: "Market resolution handling",
    body: "When a Polymarket market resolves, MarketTrace detects the resolution on the next sync cycle and updates the market status to resolved. Associated signals are marked with a resolved lifecycle status and excluded from the default actionable feed. The resolution pipeline runs on a dedicated scheduler job and also triggers immediately after referenced-market refreshes. A trust health repair endpoint can retroactively clean up signals that were missed during resolution.",
  },
  {
    title: "Read-only & trust boundaries",
    body: "MarketTrace is a read-only analytics platform. It does not execute trades, hold funds, manage wallets, or provide individualized financial advice. There is no copy-trading, no trade execution, and no custody. All data is sourced from public Polymarket APIs. Public blockchain activity may be incomplete, delayed, or misattributed. Nothing on this site is a recommendation to trade. Every score is decomposable — missing data is labeled, never invented.",
  },
  {
    title: "Risk & legal disclosure",
    body: "MarketTrace is a read-only analytics and decision-support product. It does not custody funds, execute trades, or provide individualized financial advice. Public blockchain activity may be incomplete, delayed, or misattributed. Nothing on this site is a recommendation to trade.",
  },
  {
    title: "Formula version history",
    body: "Trader scoring formula: v1.0.0. Signal formula: v1.0.0. Every score record persists the formula version used at the time of calculation.",
  },
];

export default function Methodology() {
  const { user } = useAuth();
  return (
    <div className="min-h-screen bg-[#09090b] text-zinc-100">
      {!user && (
        <header className="blur-surface border-b border-zinc-800 h-14 flex items-center px-5">
          <Link to="/" className="text-xs text-zinc-400 hover:text-zinc-100 inline-flex items-center gap-1">
            <ArrowLeft size={12} /> MarketTrace
          </Link>
        </header>
      )}
      <div className="max-w-3xl mx-auto px-5 py-12 space-y-8">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">Transparent by design</div>
          <h1 className="font-heading text-3xl font-bold text-zinc-100 mt-1">Methodology</h1>
        </div>
        <div className="space-y-8">
          {SECTIONS.map((s) => (
            <section key={s.title} data-testid={`method-${s.title}`}>
              <h2 className="font-heading text-lg font-semibold text-zinc-200">{s.title}</h2>
              <p className="text-sm text-zinc-400 mt-2 leading-relaxed">{s.body}</p>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}
